import { useCallback, useMemo, useRef, useState } from 'react';
import type { FieldPath, FieldValues, UseFormGetValues, UseFormSetError } from 'react-hook-form';

import type { ApiFieldError } from '@/api/error.api';
import { normalizeApiError } from '@/utils/error';
import {
  createServerErrorsStore,
  translateFieldError,
  translateGlobalError,
  type ServerErrorsStore,
} from '@/utils/errors';
import { getValueByPath, hasValueByPath } from '@/utils/object';

type UseApiFormErrorsParams<TFieldValues extends FieldValues> = {
  setError: UseFormSetError<TFieldValues>;
  getValues: UseFormGetValues<TFieldValues>;
  isVisibleField: (path: string) => boolean;
  fallbackMessage?: string;
  serverErrors?: ServerErrorsStore;
};

export const useServerErrorsStore = (): ServerErrorsStore => {
  const storeRef = useRef<ServerErrorsStore | null>(null);
  if (!storeRef.current) storeRef.current = createServerErrorsStore();
  return storeRef.current;
};

export type UseApiFormErrorsResult<TFieldValues extends FieldValues> = {
  applyApiError: (error: unknown) => void;
  globalError: string;
  clearGlobalError: () => void;
  clearApiErrors: () => void;
  serverErrors: ServerErrorsStore;
  setFieldError: (path: FieldPath<TFieldValues>, message: string) => void;
};

export const useApiFormErrors = <TFieldValues extends FieldValues>({
  setError,
  getValues,
  isVisibleField,
  fallbackMessage,
  serverErrors: providedServerErrors,
}: UseApiFormErrorsParams<TFieldValues>): UseApiFormErrorsResult<TFieldValues> => {
  const [globalError, setGlobalError] = useState('');
  const ownServerErrors = useServerErrorsStore();
  const serverErrors = providedServerErrors ?? ownServerErrors;

  const clearGlobalError = useCallback(() => setGlobalError(''), []);

  const clearApiErrors = useCallback(() => {
    serverErrors.clear();
    setGlobalError('');
  }, [serverErrors]);

  const applyFieldError = useCallback(
    (path: string, message: string, shouldFocus = false) => {
      serverErrors.set(path, message, getValueByPath(getValues(), path));
      setError(path as FieldPath<TFieldValues>, { type: 'server', message }, { shouldFocus });
    },
    [getValues, serverErrors, setError],
  );

  const setFieldError = useCallback(
    (path: FieldPath<TFieldValues>, message: string) => applyFieldError(path, message),
    [applyFieldError],
  );

  const applyApiError = useCallback(
    (error: unknown) => {
      const apiError = normalizeApiError(error, fallbackMessage);
      const values = getValues();
      const fieldErrors: ApiFieldError[] = apiError.fieldErrors ?? [];
      const orphanMessages: string[] = [];
      let hasFocusedFirstField = false;

      fieldErrors.forEach((fieldError) => {
        const message = translateFieldError(fieldError);

        if (!isVisibleField(fieldError.field) || !hasValueByPath(values, fieldError.field)) {
          orphanMessages.push(message);
          return;
        }

        applyFieldError(fieldError.field, message, !hasFocusedFirstField);
        hasFocusedFirstField = true;
      });

      const hasVisibleFieldErrors = fieldErrors.length > orphanMessages.length;
      if (hasVisibleFieldErrors && !orphanMessages.length) {
        setGlobalError('');
        return;
      }

      const globalMessage = translateGlobalError({
        code: apiError.code,
        userMessage: apiError.userMessage,
        fallbackMessage,
      });
      const uniqueOrphans = [...new Set(orphanMessages)];

      setGlobalError(
        uniqueOrphans.length ? `${globalMessage}: ${uniqueOrphans.join('; ')}` : globalMessage,
      );
    },
    [applyFieldError, fallbackMessage, getValues, isVisibleField],
  );

  return useMemo(
    () => ({
      applyApiError,
      globalError,
      clearGlobalError,
      clearApiErrors,
      serverErrors,
      setFieldError,
    }),
    [applyApiError, clearApiErrors, clearGlobalError, globalError, serverErrors, setFieldError],
  );
};
